const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Forms/store.ts';
import Imm from 'immutable';
import { ReducerState } from '../../defs/actionTypes';
import {
    getData,
    getDataAsMap,
    getDataFromPayloadWithListObject,
    getRequestStatus,
    makeReducer2,
    useRemoteData2,
} from '../../utils/redux4Requests';
import { createSelector } from 'reselect';
import { CommonStoreState } from '../../reducers';
import {
    actionClearSearchUserForForm,
    actionLoadMetaKatBlockForForm,
    actionSearchUserForForm,
    CLEAR_SEARCH_USER_FOR_FORM,
    ClearSearchUserForFormAction,
    LOAD_FROM_METAKAT_FOR_FORM,
    LoadMetaKatChildrenForFormAction,
    SEARCH_USER_FOR_FORM,
    SearchUserForFormAction,
} from './actions';
import { MetaKatItem, User } from '../../defs/kisim';
import { filterInactiveAndIncompleteMetaKatItems } from './utils';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMadeSelector } from '@cistec/common_web/utils/store';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';
import _ from 'lodash';

export const KEY_METAKAT_DATA = 'metaKat';
const KEY_USER_SEARCH = 'userSearch';

export const formReducer = makeReducer2(
    [
        [
            LOAD_FROM_METAKAT_FOR_FORM,
            (action: LoadMetaKatChildrenForFormAction) => [KEY_METAKAT_DATA, action.meta.pcode],
            getDataFromPayloadWithListObject,
        ],
        [
            SEARCH_USER_FOR_FORM,
            (action: SearchUserForFormAction) => [KEY_USER_SEARCH, action.meta.contextId],
            getDataFromPayloadWithListObject,
        ],
    ],
    {
        [CLEAR_SEARCH_USER_FOR_FORM]: (state: ReducerState, action: ClearSearchUserForFormAction) => {
            return state.removeIn([KEY_USER_SEARCH, action.meta.contextId]);
        },
    }
);

// SELECTORS

const selectReducerState = (state: CommonStoreState): ReducerState => state.form;

export const makeSelectFormMetaKatData = (iCode: string) =>
    createSelector([selectReducerState], (state: ReducerState) => {
        const metaKatItems: MetaKatItem[] | undefined = getData(state, [KEY_METAKAT_DATA, iCode]);
        return metaKatItems ? filterInactiveAndIncompleteMetaKatItems(metaKatItems) : undefined;
    });

export function useFormMetaKatData(iCode: string, lookup: boolean = false) {
    const select = useMemo(() => makeSelectFormMetaKatData(iCode), [iCode]);
    return useRemoteData2('form', {
        canRequest: !!iCode,
        request: dispatch => dispatch(actionLoadMetaKatBlockForForm(iCode, lookup)),
        keys: [KEY_METAKAT_DATA, iCode],
        select,
    });
}

export const makeSelectImmutableFormMetaKatData = (iCode: string) => {
    return createSelector([selectReducerState], (state: ReducerState) => {
        const dataMap = getDataAsMap(state, [KEY_METAKAT_DATA]);
        if (dataMap) {
            return Imm.fromJS(dataMap).get(iCode);
        }
        return undefined;
    });
};

const makeSelectUserSearchRequestStatus = (contextId: string) =>
    createSelector([selectReducerState], (state: ReducerState) => {
        return getRequestStatus(state, [KEY_USER_SEARCH, contextId]);
    });

const makeSelectUserSearchData = (contextId: string) =>
    createSelector([selectReducerState], (state: ReducerState): KeyValuePair<User> => {
        return getData(state, [KEY_USER_SEARCH, contextId]);
    });

export function useUserSearch() {
    const dispatch = useDispatch();
    const [contextId] = useState(uuidv4());

    const requestStatus = useMadeSelector(() => makeSelectUserSearchRequestStatus(contextId), [contextId]);
    const data = useMadeSelector(() => makeSelectUserSearchData(contextId), [contextId]);

    useEffect(() => {
        return () => {
            dispatch(actionClearSearchUserForForm(contextId));
        };
    }, []);

    const runSearch = useCallback((searchText: string, filterByZUSUSRSEL?: boolean) => {
        dispatch(actionSearchUserForForm(contextId, searchText, filterByZUSUSRSEL));
    }, []);

    const debouncedRunSearch = useMemo(() => _.debounce(runSearch, 300), [runSearch]);

    return {
        data,
        requestStatus,
        search: (searchText: string, filterByZUSUSRSEL?: boolean) => {
            if (searchText.length < 3) {
                dispatch(actionClearSearchUserForForm(contextId));
            } else {
                debouncedRunSearch(searchText, filterByZUSUSRSEL);
            }
        },
    };
}
